/**
 * Outline view — the mock's three-column table of the decision tree.
 *
 * NOT a simplified canvas. The canvas answers "what is the shape of this
 * questionnaire"; this answers "what does each answer actually DO", by flattening
 * every question into its answers and putting the rule that fires next to them. That
 * is the view for reviewing rules, which is most of what an admin does here, and it
 * was missing entirely from the first cut.
 *
 * Columns, per the mock: 问题 | 答案 | 判断逻辑.
 *
 *  - A QUESTION row is the parent: bold, expandable, its 判断逻辑 cell carries the
 *    threshold the node's rules test.
 *  - An ANSWER row is a child: 问题 blank, 答案 showing the answer label and — when
 *    one of the node's program rules keys on that answer — the programs it grants,
 *    rendered the way the mock writes them (`是，#1 先咨询 SCE-led Cross PPA`).
 *
 * The mock shows a read-only table with checkboxes and per-column filters. The
 * checkboxes have no action behind them in the mock and none here; they are left out
 * rather than rendered inert, because a checkbox that does nothing reads as a bug.
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Link, SpaceBetween, Table, TextFilter } from '@amzn/awsui-components-react';

import type { QuestionnaireDefinition, QuestionnaireNode } from '../../types';
import { humanCondition, metricRefsOf } from '../../utils/projection';
import './OutlineView.css';

type Row =
  | { kind: 'question'; key: string; node: QuestionnaireNode; logic: string; expanded: boolean }
  | { kind: 'answer'; key: string; node: QuestionnaireNode; answer: string; grants: string[] };

export interface OutlineViewProps {
  definition: QuestionnaireDefinition;
  onSelectNode: (nodeId: string) => void;
}

/**
 * The thresholds a node's rules test, as one short phrase.
 *
 * Only comparisons are surfaced — those are what the mock's 判断逻辑 column shows
 * (`TCV ≥ $5,000K ?`, `总 ARR ≥ $300K ?`). An equality rule is about which answer was
 * given, which belongs on the answer row, not here.
 */
function logicOf(node: QuestionnaireNode, label0: string): string {
  const parts: string[] = [];
  for (const r of node.programRules ?? []) {
    const m = /(>=|<=|>|<)\s*(-?[\d.]+)/.exec(r.expression);
    if (!m) continue;
    const refs = metricRefsOf(r.expression);
    const label = refs.length > 1 ? label0 : (refs[0] ?? '');
    const phrase = `${label} ${m[1]} ${Number(m[2]).toLocaleString('en-US')} ?`;
    if (!parts.includes(phrase)) parts.push(phrase);
  }
  return parts.join('；') || '-';
}

/** Programs granted by rules that key on this specific answer value. */
function grantsForAnswer(node: QuestionnaireNode, value: string): string[] {
  const out: string[] = [];
  for (const r of node.programRules ?? []) {
    if (!r.expression.includes(`'${value}'`)) continue;
    for (const v of r.values) if (!out.includes(v)) out.push(v);
  }
  return out;
}

export default function OutlineView({ definition, onSelectNode }: OutlineViewProps) {
  const { t } = useTranslation('cpa');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState('');

  /**
   * Answer values → their labels, keyed by `qualifiedNodeId` because that is the name
   * expressions reference (it is not the `nodeId`). Without this the 下一题 column
   * showed the bare stored value — `a`, `b` — while the canvas showed the real option
   * text for the same rule, so the two views disagreed about the same edge.
   */
  const optionLabelOf = useMemo(() => {
    const byQualifiedId = new Map<string, QuestionnaireNode>();
    for (const n of definition.nodes) {
      if (n.qualifiedNodeId) byQualifiedId.set(n.qualifiedNodeId, n);
    }
    return (qualifiedId: string, value: string) => {
      const opt = byQualifiedId.get(qualifiedId)?.answerOptions?.find((o) => o.value === value);
      return opt ? opt.optionText.cn || opt.optionText.en : undefined;
    };
  }, [definition.nodes]);

  const rows = useMemo(() => {
    const out: Row[] = [];
    for (const n of definition.nodes) {
      const isCollapsed = collapsed.has(n.nodeId);
      out.push({
        kind: 'question',
        key: n.nodeId,
        node: n,
        logic: logicOf(n, t('outlineCombined')),
        expanded: !isCollapsed,
      });
      if (isCollapsed) continue;
      for (const o of n.answerOptions ?? []) {
        out.push({
          kind: 'answer',
          key: `${n.nodeId}::${o.optionId}`,
          node: n,
          answer: o.optionText.cn || o.optionText.en,
          grants: grantsForAnswer(n, o.value),
        });
      }
    }
    if (!filter.trim()) return out;
    const q = filter.trim().toLowerCase();
    // Filter on the QUESTION, keeping its answers: filtering answers independently
    // would strand child rows under a parent that was filtered away.
    const keep = new Set(
      definition.nodes
        .filter((n) =>
          [n.nodeId, n.qualifiedNodeId, n.questionText.cn, n.questionText.en]
            .filter(Boolean)
            .some((v) => v!.toLowerCase().includes(q)),
        )
        .map((n) => n.nodeId),
    );
    return out.filter((r) => keep.has(r.node.nodeId));
  }, [definition.nodes, collapsed, filter, t]);

  const toggle = (nodeId: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });

  return (
    <SpaceBetween size="s">
      <TextFilter
        filteringText={filter}
        filteringPlaceholder={t('outlineFilterPlaceholder')}
        countText={t('outlineCount', {
          count: rows.filter((r) => r.kind === 'question').length,
        })}
        onChange={({ detail }) => setFilter(detail.filteringText)}
      />
      <Table
        variant="embedded"
        items={rows}
        trackBy="key"
        empty={<Box color="text-status-inactive">{t('outlineEmpty')}</Box>}
        columnDefinitions={[
          {
            id: 'question',
            header: t('outlineColQuestion'),
            width: 420,
            cell: (r) =>
              r.kind === 'question' ? (
                <span className="cpa-outline__q">
                  <Button
                    variant="inline-icon"
                    iconName={r.expanded ? 'caret-down-filled' : 'caret-right-filled'}
                    ariaLabel={r.expanded ? t('outlineCollapse') : t('outlineExpand')}
                    onClick={() => toggle(r.node.nodeId)}
                  />
                  <Link onFollow={() => onSelectNode(r.node.nodeId)}>
                    <strong>{r.node.questionText.cn || r.node.questionText.en}</strong>
                  </Link>
                </span>
              ) : null,
          },
          {
            id: 'answer',
            header: t('outlineColAnswer'),
            cell: (r) =>
              r.kind === 'answer' ? (
                <span className="cpa-outline__a">
                  {r.answer}
                  {r.grants.length > 0 && (
                    <span className="cpa-outline__grants">，{r.grants.join(' + ')}</span>
                  )}
                </span>
              ) : null,
          },
          {
            id: 'logic',
            header: t('outlineColLogic'),
            width: 260,
            cell: (r) =>
              r.kind === 'question' ? (
                <span className="cpa-outline__logic">{r.logic}</span>
              ) : null,
          },
          {
            id: 'next',
            header: t('outlineColNext'),
            width: 200,
            cell: (r) => {
              if (r.kind !== 'question') return null;
              const n = r.node;
              if (n.isLastNode)
                return <span className="cpa-outline__logic">{t('outlineTerminal')}</span>;
              if (n.nextNodeId) return <code>{n.nextNodeId}</code>;
              return (
                <SpaceBetween size="xxs">
                  {(n.nextQuestionRules ?? []).map((rule, i) => (
                    <span key={i} className="cpa-outline__edge">
                      <b>{i + 1}</b>
                      {humanCondition(rule.expression, optionLabelOf) ?? rule.expression}
                      {' → '}
                      <code>{rule.nextQuestionId}</code>
                    </span>
                  ))}
                </SpaceBetween>
              );
            },
          },
        ]}
      />
    </SpaceBetween>
  );
}
